import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Layers, Eye } from "lucide-react";
import { api } from "../lib/api";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "branding", label: "Branding" },
  { id: "production", label: "Production" },
  { id: "reels", label: "Reels" },
  { id: "shoots", label: "Shoots" },
  { id: "posters", label: "Posters" },
  { id: "websites", label: "Websites" },
];

const CATEGORY_COLORS = {
  branding:   "bg-pink-100 text-pink-700",
  production: "bg-orange-100 text-orange-700",
  reels:      "bg-cyan-100 text-cyan-700",
  shoots:     "bg-violet-100 text-violet-700",
  posters:    "bg-emerald-100 text-emerald-700",
  websites:   "bg-blue-100 text-blue-700",
};

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [all, setAll] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/portfolio").then((r) => setAll(r.data));
  }, []);

  useEffect(() => {
    if (filter === "all") {
      setItems(all);
    } else {
      setItems(all.filter((p) => p.category === filter));
    }
  }, [filter, all]);

  const categories = [...new Set(all.map((p) => p.category))];
  const clients = [...new Set(all.map((p) => p.client).filter(Boolean))];

  return (
    <div data-testid="portfolio-page" className="pt-24">

      {/* HERO */}
      <section className="container mx-auto px-4 pt-16 pb-10">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF007F] mb-3">Portfolio</div>
        <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-none mb-4">
          Stories we've <span className="text-gradient-animate">brought to life.</span>
        </h1>
        <p className="text-lg text-zinc-600 font-medium max-w-2xl mb-10">
          From brand identity to cinematic reels, every project is built to make an impact.
          Browse our work across industries.
        </p>

        {/* Stats row */}
        <div className="flex flex-wrap gap-6">
          {[
            { num: all.length,         label: "Projects" },
            { num: categories.length,  label: "Categories" },
            { num: clients.length,     label: "Clients" },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-3 bg-zinc-50 border border-zinc-200 rounded-2xl px-5 py-3">
              <span className="font-display font-black text-3xl text-gradient">{s.num}+</span>
              <span className="text-sm font-semibold text-zinc-600">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* GRID */}
      <section className="container mx-auto px-4 pb-24">

        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-8 border-b border-zinc-200">
          {FILTERS.map((f) => {
            const count = f.id === "all" ? all.length : all.filter((p) => p.category === f.id).length;
            return (
              <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`pf-filter-${f.id}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition flex items-center gap-1.5 ${
                  filter === f.id ? "bg-[#0A1128] text-white" : "bg-zinc-100 hover:bg-zinc-200 text-zinc-700"
                }`}>
                {f.label}
                <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full ${filter === f.id ? "bg-white/20" : "bg-zinc-300"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {(() => {
          const cols = 3;
          const remainder = items.length % cols;
          const placeholders = remainder === 0 ? 0 : cols - remainder;
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((p, idx) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: idx * 0.05 }}
                  data-testid={`pf-item-${p.id}`}
                  className="group relative aspect-square overflow-hidden rounded-2xl shadow-sm hover:shadow-xl transition-shadow duration-500"
                >
                  {/* Image */}
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />

                  {/* Always-visible bottom strip */}
                  <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm px-4 py-3 flex items-center justify-between gap-2 translate-y-0 group-hover:translate-y-full transition duration-400">
                    <div>
                      <div className="font-display font-bold text-sm text-[#0A1128] leading-tight truncate">{p.title}</div>
                      <div className="text-xs text-zinc-500 font-medium mt-0.5">{p.client}</div>
                    </div>
                    <span className={`text-[10px] font-mono uppercase tracking-wide px-2 py-1 rounded-full shrink-0 font-semibold ${CATEGORY_COLORS[p.category] || "bg-zinc-100 text-zinc-600"}`}>
                      {p.category}
                    </span>
                  </div>

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition duration-400 flex flex-col justify-end p-5">
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {p.tags?.map((t) => (
                        <span key={t} className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 bg-white/20 backdrop-blur rounded-full text-white">{t}</span>
                      ))}
                    </div>
                    <div className="font-display font-bold text-xl text-white leading-tight mb-0.5">{p.title}</div>
                    <div className="text-xs text-white/70 mb-4">{p.client}</div>
                    <div className="flex items-center gap-2 text-xs font-bold text-white bg-white/20 backdrop-blur w-fit px-3 py-1.5 rounded-full">
                      <Eye className="w-3.5 h-3.5" /> View Project
                    </div>
                  </div>
                </motion.div>
              ))}

              {Array.from({ length: placeholders }).map((_, i) => (
                <div key={`ph-${i}`}
                  className="aspect-square rounded-2xl bg-zinc-50 flex flex-col items-center justify-center gap-3 border-2 border-dashed border-zinc-300">
                  <div className="w-12 h-12 rounded-full bg-zinc-200 flex items-center justify-center">
                    <svg className="w-6 h-6 text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3 3h18M3 21h18" />
                    </svg>
                  </div>
                  <span className="text-xs font-mono uppercase tracking-widest text-zinc-400 font-semibold">Coming Soon</span>
                </div>
              ))}
            </div>
          );
        })()}
      </section>

      {/* CTA BAND */}
      <section className="bg-[#0A1128] text-white py-20">
        <div className="container mx-auto px-4 text-center max-w-2xl">
          <Layers className="w-10 h-10 text-[#FF007F] mx-auto mb-4" />
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight mb-4">
            Want your brand <span className="text-gradient-animate">in here?</span>
          </h2>
          <p className="text-white/70 font-medium mb-8">
            Let's build something worth showing off. Tell us about your project and we'll craft a strategy to match.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-brand text-white font-bold hover:opacity-90 transition">
              Start a Project <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/quote"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-white/30 text-white font-bold hover:border-white transition">
              Get AI Quote
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}
